import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DocumentTemplateService as ComponentService } from '../document-template.service';
import { AuthService } from 'src/app/services/auth.service';
import { finalize, forkJoin, map, takeUntil, Subject, debounceTime, distinctUntilChanged, startWith } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatButtonToggleChange } from '@angular/material/button-toggle';

import Swal from 'sweetalert2';
import {
    DocumentTypeFunctieAssignment,
    AssignmentDialogData,
    FunctieData,
    DirectieData,
    CompartimentData,
    UserData,
    AvailableSelectionsData,
    AssignmentValidationError
} from '../../document-types/document-types.interface';
import { DocumentTypesService } from '../../document-types/document-types.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
// Note: FormBuilder and FormArray removed as they're no longer used after simplifying to [(ngModel)] approach

export interface OrderSignature {
    id: number;
    user_type: number;
    order: number;
    sign_type: null | number | string;
    functie: string;
    template_id: number;
    created_at: string;
    updated_at: string;
    // New optional fields for enhanced assignments
    directie_id?: number | null;
    compartiment_id?: number | null;
    user_id?: number | null;
    // Helper fields for display (populated from API or lookups)
    directie_name?: string;
    compartiment_name?: string;
    user_name?: string;
}

@Component({
    selector: 'app-document-template-signatures',
    templateUrl: './document-template-signatures.component.html',
    styleUrls: ['./document-template-signatures.component.scss'],
})
export class DocumentTemplateSignaturesComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject<void>();

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    successTitle: string = environment.config.customNotifications.headers.success;
    successIcon: string = environment.config.customNotifications.icons.success;
    successType: string = environment.config.customNotifications.icons.success;

    // Store compartimente and users per directie/compartiment for better management
    compartimenteByDirectie: { [directieId: number]: CompartimentData[] } = {};
    usersByDirectieCompartiment: { [key: string]: UserData[] } = {};

    // Typeahead search controls (per row index)
    directiiSearchControls: { [index: number]: FormControl } = {};
    compartimenteSearchControls: { [index: number]: FormControl } = {};
    usersSearchControls: { [index: number]: FormControl } = {};

    // Filtered data for typeahead (per row index)
    filteredDirectiiByIndex: { [index: number]: DirectieData[] } = {};
    filteredCompartimenteByIndex: { [index: number]: CompartimentData[] } = {};
    filteredUsersByIndex: { [index: number]: UserData[] } = {};

    helper = helper;

    token!: string;
    isLoading = false;
    isDragging = false;

    form: FormGroup = new FormGroup({
        name: new FormControl(this.data.name, [Validators.required]),
        context: new FormControl(this.data.context, [Validators.required]),
        type_id: new FormControl(this.data.type_id, [Validators.required]),
    });

    isChecked: boolean = false;
    saveClicked: boolean = false;
    functiiSelectedOrder: OrderSignature[] = [];
    functiiOperator: any = [];
    functiiLoaded: boolean = false;

    // Data sources for new dropdown fields
    directiiList: DirectieData[] = [];

    // Cache for compartimente and users (already exists in lines 65-66, keeping reference here)
    // compartimenteByDirectie is already declared
    // usersByDirectieCompartiment is already declared

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };
    destinationToggle: string = 'toPrimarie'

    onSwitchChange(event: MatButtonToggleChange) {
        this.destinationToggle = event.value;
        this.loadDataTemplate();
    }

    manual_override: boolean = false
    doc_type: number = 1

    documentTypesTranslation: any = {
        'toPrimarie': 1,
        'toCetatean': 2
    }
    documentTypesTranslationReverse: any = {
        1: 'toPrimarie',
        2: 'toCetatean'
    }

    signatureTypes: any[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: any,
        private dialogRef: MatDialogRef<DocumentTemplateSignaturesComponent>,
        private componentService: ComponentService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private documentTypesService: DocumentTypesService,
        // Note: FormBuilder removed as FormArray-based approach was replaced with simpler [(ngModel)] binding
    ) {
        console.log('data', data)
        this.processToken();

        if (typeof this.data.manual_override !== 'undefined') {
            this.manual_override = this.data.manual_override

            this.destinationToggle = this.documentTypesTranslationReverse[this.data.doc_type]
            this.doc_type = this.data.doc_type
            console.log('doc_type', this.doc_type);
            console.log('this.manual_override', this.manual_override);
            console.log('this.data.id', this.data.id);
            console.log('destinationToggle', this.destinationToggle)
        }

        // COMMENTED OUT: assignmentForm initialization (part of old FormArray implementation)
        // this.assignmentForm = this.fb.group({
        //     assignments: this.fb.array([])
        // });

    }

    ngOnInit(): void {
        this.loadSignatureTypes();
        this.loadDataTemplate();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    /* ========================================================================
       COMMENTED OUT: Old FormArray-based implementation methods (not used)
       These methods were part of the complex FormArray + TypeaheadControls approach
       that has been replaced with a simpler [(ngModel)] binding approach.
       Keeping for reference in case needed in the future.
       ========================================================================

    private loadInitialData(): void {
        this.loading = true;

        // Load available functii, directii
        Promise.all([
            // this.loadAvailableFunctii(),
            this.loadAvailableDirectii()
        ]).then(async () => {
            await this.initializeExistingAssignments();
        }).catch((error) => {
            console.error('Error loading initial data:', error);
            this.showErrorMessage('Eroare la încărcarea datelor inițiale');
        }).finally(() => {
            this.loading = false;
        });
    }

    private async loadAvailableDirectii(): Promise<void> {
        try {
            const response = await this.documentTypesService.getAvailableDirectii().toPromise();
            if (response?.success) {
                this.availableData.directii = response.data;
            }
        } catch (error) {
            console.error('Error loading directii:', error);
            throw error;
        }
    }

    private async initializeExistingAssignments(): Promise<void> {
        this.assignmentsArray.clear();

        if (this.data.existingAssignments?.length > 0) {
            // Process existing assignments sequentially to ensure proper loading
            for (const assignment of this.data.existingAssignments) {
                await this.addAssignmentFormGroupWithData(assignment);
            }
        } else {
            // Add one empty assignment to start with
            this.addAssignmentFormGroup();
        }
    }

    get assignmentsArray(): FormArray {
        return this.assignmentForm.get('assignments') as FormArray;
    }

    private addAssignmentFormGroup(assignment?: DocumentTypeFunctieAssignment): void {
        const assignmentGroup = this.fb.group({
            id: [assignment?.id || null],
            functie_id: [assignment?.functie_id || null, Validators.required],
            directie_id: [assignment?.directie_id || null, Validators.required],
            compartiment_id: [assignment?.compartiment_id || -1],
            user_id: [assignment?.user_id || -1],
            order_priority: [assignment?.order_priority || this.assignmentsArray.length + 1]
        });

        const currentIndex = this.assignmentsArray.length;

        // Set up typeahead controls
        this.setupTypeaheadControls(currentIndex);

        // Set up cascading dropdowns
        this.setupCascadingSelections(assignmentGroup, currentIndex);

        this.assignmentsArray.push(assignmentGroup);

        // Load initial compartimente and users if directie is already selected
        if (assignment?.directie_id) {
            this.loadCompartimenteForAssignment(currentIndex, assignment.directie_id);
            if (assignment.compartiment_id) {
                this.loadUsersForAssignment(currentIndex, assignment.directie_id, assignment.compartiment_id);
            } else {
                this.loadUsersForAssignment(currentIndex, assignment.directie_id);
            }
        }
    }

    private setupTypeaheadControls(index: number): void {
        // Initialize controls for this assignment
        this.directiiControls[index] = new FormControl();
        this.compartimenteControls[index] = new FormControl();
        this.usersControls[index] = new FormControl();

        // Initialize filtered arrays
        this.filteredDirectiiByIndex[index] = [...this.availableData.directii];
        this.filteredCompartimenteByIndex[index] = [];
        this.filteredUsersByIndex[index] = [];

        // Setup typeahead for Functii

        // Setup typeahead for Directii
        this.directiiControls[index].valueChanges.pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredDirectiiByIndex[index] = [...this.availableData.directii];
                    return of(this.availableData.directii);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectiiByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Setup typeahead for Compartimente
        this.compartimenteControls[index].valueChanges.pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredCompartimenteByIndex[index] = this.getCompartimenteForCurrentAssignment(index);
                    return of(this.getCompartimenteForCurrentAssignment(index));
                }
                return this.filterCompartimente(inputValue, index);
            }),
            tap((data: any[]) => {
                this.filteredCompartimenteByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        // Setup typeahead for Users
        this.usersControls[index].valueChanges.pipe(
            takeUntil(this.unsubscribe$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredUsersByIndex[index] = this.getUsersForCurrentAssignment(index);
                    return of(this.getUsersForCurrentAssignment(index));
                }
                return this.filterUsers(inputValue, index);
            }),
            tap((data: any[]) => {
                this.filteredUsersByIndex[index] = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    private setupCascadingSelections(formGroup: FormGroup, index: number): void {
        // Watch directie changes
        formGroup.get('directie_id')?.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((directieId) => {
                if (directieId) {
                    // Clear dependent fields
                    formGroup.patchValue({
                        compartiment_id: -1,
                        user_id: -1
                    });

                    // Load compartimente
                    this.loadCompartimenteForAssignment(index, directieId);

                    // Load users for directie only
                    this.loadUsersForAssignment(index, directieId);
                } else {
                    // Clear all dependent data
                    this.clearAssignmentSelections(index);
                }
            });

        // Watch compartiment changes
        formGroup.get('compartiment_id')?.valueChanges
            .pipe(
                takeUntil(this.unsubscribe$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((compartimentId) => {
                const directieId = formGroup.get('directie_id')?.value;
                if (directieId) {
                    // Clear user selection
                    formGroup.patchValue({ user_id: -1 });

                    // Load filtered users
                    this.loadUsersForAssignment(index, directieId, compartimentId);
                }
            });
    }

    private async loadCompartimenteForAssignment(assignmentIndex: number, directieId: number): Promise<void> {
        try {
            // Check if we already have compartimente for this directie
            if (!this.compartimenteByDirectie[directieId]) {
                const response = await this.documentTypesService.getCompartimenteByDirectie(directieId).toPromise();
                if (response?.success) {
                    this.compartimenteByDirectie[directieId] = response.data;
                }
            }

            // Update filtered compartimente for this assignment
            this.filteredCompartimenteByIndex[assignmentIndex] = this.compartimenteByDirectie[directieId] || [];
        } catch (error) {
            console.error('Error loading compartimente:', error);
        }
    }

    private async loadUsersForAssignment(assignmentIndex: number, directieId: number, compartimentId?: number): Promise<void> {
        try {
            const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
            const cacheKey = `${directieId}_${actualCompartimentId || 'null'}`;

            // Check if we already have users for this combination
            if (!this.usersByDirectieCompartiment[cacheKey]) {
                const response = await this.documentTypesService.getAvailableUsers(
                    this.data.documentTypeId,
                    directieId,
                    actualCompartimentId
                ).toPromise();

                if (response?.success) {
                    this.usersByDirectieCompartiment[cacheKey] = response.data;
                }
            }

            // Update filtered users for this assignment
            this.filteredUsersByIndex[assignmentIndex] = this.usersByDirectieCompartiment[cacheKey] || [];
        } catch (error) {
            console.error('Error loading users:', error);
        }
    }

    private clearAssignmentSelections(assignmentIndex: number): void {
        // Clear filtered data for this assignment
        this.filteredCompartimenteByIndex[assignmentIndex] = [];
        this.filteredUsersByIndex[assignmentIndex] = [];
    }

    // Typeahead filter methods
    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const filtered = this.availableData.directii.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterCompartimente(searchTerm: string, assignmentIndex: number) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const availableCompartimente = this.getCompartimenteForCurrentAssignment(assignmentIndex);
        const filtered = availableCompartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    filterUsers(searchTerm: string, assignmentIndex: number) {
        const normalizedSearchTerm = this.normalizeString(searchTerm);
        const availableUsers = this.getUsersForCurrentAssignment(assignmentIndex);
        const filtered = availableUsers.filter(user =>
            this.normalizeString(user.name || user.full_name || '').includes(normalizedSearchTerm)
        );
        return of(filtered);
    }

    // Get compartimente for a specific directie
    getCompartimenteForDirectie(directieId: number): CompartimentData[] {
        return this.compartimenteByDirectie[directieId] || [];
    }

    // Get users for a specific directie/compartiment combination
    getUsersForDirectieCompartiment(directieId: number, compartimentId?: number): UserData[] {
        const cacheKey = `${directieId}_${compartimentId === -1 || !compartimentId ? 'null' : compartimentId}`;
        return this.usersByDirectieCompartiment[cacheKey] || [];
    }

    // Get compartimente for current assignment based on selected directie
    getCompartimenteForCurrentAssignment(assignmentIndex: number): CompartimentData[] {
        const assignment = this.assignmentsArray.at(assignmentIndex);
        const directieId = assignment?.get('directie_id')?.value;
        return directieId ? this.getCompartimenteForDirectie(directieId) : [];
    }

    // Get users for current assignment based on selected directie/compartiment
    getUsersForCurrentAssignment(assignmentIndex: number): UserData[] {
        const assignment = this.assignmentsArray.at(assignmentIndex);
        const directieId = assignment?.get('directie_id')?.value;
        const compartimentId = assignment?.get('compartiment_id')?.value;
        const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
        return directieId ? this.getUsersForDirectieCompartiment(directieId, actualCompartimentId) : [];
    }

    private async addAssignmentFormGroupWithData(assignment: DocumentTypeFunctieAssignment): Promise<void> {
        const assignmentGroup = this.fb.group({
            id: [assignment?.id || null],
            directie_id: [assignment?.directie_id || null, Validators.required],
            compartiment_id: [assignment?.compartiment_id || -1],
            user_id: [assignment?.user_id || -1],
            order_priority: [assignment?.order_priority || this.assignmentsArray.length + 1]
        });

        const currentIndex = this.assignmentsArray.length;

        // Set up typeahead controls
        this.setupTypeaheadControls(currentIndex);

        // Set up cascading dropdowns
        this.setupCascadingSelections(assignmentGroup, currentIndex);

        this.assignmentsArray.push(assignmentGroup);

        // Load cascading data sequentially for existing assignments
        if (assignment?.directie_id) {
            // Load compartimente first
            await this.loadCompartimenteForAssignment(currentIndex, assignment.directie_id);

            // Then load users (with or without compartiment)
            if (assignment.compartiment_id) {
                await this.loadUsersForAssignment(currentIndex, assignment.directie_id, assignment.compartiment_id);
            } else {
                await this.loadUsersForAssignment(currentIndex, assignment.directie_id);
            }
        }
    }

    onDirectieSelectClosed(assignmentIndex: number) {
        const selectedValue = this.directiiControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredDirectiiByIndex[assignmentIndex].some(directie =>
                directie.id_directie === assignment?.get('directie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControls[assignmentIndex].setValue(null);
                this.filteredDirectiiByIndex[assignmentIndex] = [...this.availableData.directii];
            }
        }
    }

    onCompartimentSelectClosed(assignmentIndex: number) {
        const selectedValue = this.compartimenteControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredCompartimenteByIndex[assignmentIndex].some(compartiment =>
                compartiment.id_compartiment === assignment?.get('compartiment_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControls[assignmentIndex].setValue(null);
                this.filteredCompartimenteByIndex[assignmentIndex] = this.getCompartimenteForCurrentAssignment(assignmentIndex);
            }
        }
    }

    onUserSelectClosed(assignmentIndex: number) {
        const selectedValue = this.usersControls[assignmentIndex].value;
        if (selectedValue) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const isSelectedInFilteredList = this.filteredUsersByIndex[assignmentIndex].some(user =>
                user.id === assignment?.get('user_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControls[assignmentIndex].setValue(null);
                this.filteredUsersByIndex[assignmentIndex] = this.getUsersForCurrentAssignment(assignmentIndex);
            }
        }
    }

    // Load compartimente when compartiment dropdown is opened
    async onCompartimentDropdownOpened(opened: boolean, assignmentIndex: number): Promise<void> {
        if (opened) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const directieId = assignment?.get('directie_id')?.value;
            if (directieId && !this.compartimenteByDirectie[directieId]) {
                await this.loadCompartimenteForAssignment(assignmentIndex, directieId);
            }
        }
    }

    // Load users when user dropdown is opened
    async onUserDropdownOpened(opened: boolean, assignmentIndex: number): Promise<void> {
        if (opened) {
            const assignment = this.assignmentsArray.at(assignmentIndex);
            const directieId = assignment?.get('directie_id')?.value;
            const compartimentId = assignment?.get('compartiment_id')?.value;
            if (directieId) {
                const actualCompartimentId = compartimentId === -1 ? undefined : compartimentId;
                const cacheKey = `${directieId}_${actualCompartimentId || 'null'}`;
                if (!this.usersByDirectieCompartiment[cacheKey]) {
                    await this.loadUsersForAssignment(assignmentIndex, directieId, actualCompartimentId);
                }
            }
        }
    }

    // end new formula
    */

    /* ========================================================================
       END OF COMMENTED OUT CODE
       ======================================================================== */

    loadSignatureTypes() {
        // For now, use hardcoded values with the new signature types
        // TODO: Replace with API call when backend is deployed
        this.signatureTypes = [
            { label: '--Alegeți--', value: '-1' },
            { label: 'Semnare', value: 1 },
            { label: 'Aprobare', value: 2 },
            { label: 'Confirmare', value: 3 },
            { label: 'Semnare olografica', value: 4 },
        ];

        // Future API implementation:
        // this.componentService.getSignatureTypes().subscribe({
        //     next: (response) => {
        //         if (response.errors === false) {
        //             this.signatureTypes = response.data;
        //         }
        //     },
        //     error: (error) => {
        //         console.error('Error loading signature types:', error);
        //         // Fallback to hardcoded values
        //         this.signatureTypes = [
        //             { label: '--Alegeți--', value: '-1' },
        //             { label: 'Semnare', value: 1 },
        //             { label: 'Aprobare', value: 2 },
        //             { label: 'Confirmare', value: 3 },
        //             { label: 'Semnare olografica', value: 4 },
        //         ];
        //     }
        // });
    }

    loadDataTemplate() {
        this.isLoading = true;
        let doc_type = this.destinationToggle === 'toPrimarie' ? 1 : 2;

        const functiiOperator$ = this.componentService
            .getFunctiiUserActiveList()
            .pipe(takeUntil(this.unsubscribe$));

        const signaturesOperator$ = this.componentService
            .signaturesTemplateOperatorListIds(this.data.id, doc_type, this.manual_override)
            .pipe(
                takeUntil(this.unsubscribe$),
                map((res: any) => res.data)
            );

        // Load directii for new dropdowns
        const directii$ = this.documentTypesService
            .getAvailableDirectii()
            .pipe(
                takeUntil(this.unsubscribe$),
                map((res: any) => res.data || [])
            );

        forkJoin({ functiiOperator$, signaturesOperator$, directii$ })
            .pipe(
                takeUntil(this.unsubscribe$),
                map(({ functiiOperator$, signaturesOperator$, directii$ }) => {
                    this.functiiSelectedOrder = signaturesOperator$.order_signatures;
                    this.functiiOperator = functiiOperator$.map((functieOp: any) => ({
                        ...functieOp,
                        checked: signaturesOperator$.ids.includes(functieOp.id),
                    }));
                    this.directiiList = directii$;

                    // Initialize typeahead controls for all existing rows after a short delay
                    // This ensures the view is rendered before we setup controls
                    setTimeout(() => {
                        this.functiiSelectedOrder.forEach((row, index) => {
                            this.setupTypeaheadForRow(index);

                            // Preload compartimente and users if directie is selected
                            // Filtered lists will be automatically updated when data loads
                            if (row.directie_id) {
                                this.loadCompartimenteForDirectie(row.directie_id);

                                const compartimentId = row.compartiment_id ?? undefined;
                                this.loadUsersForSelection(row.directie_id, compartimentId);
                            }
                        });
                    }, 0);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe();
    }

    /**
     * Load compartimente for a specific directie
     */
    loadCompartimenteForDirectie(directieId: number): void {
        // Check if already cached
        if (this.compartimenteByDirectie[directieId]) {
            return;
        }

        this.documentTypesService
            .getCompartimenteByDirectie(directieId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.compartimenteByDirectie[directieId] = res.data || [];

                    // Update filtered lists for all rows that have this directie selected
                    this.functiiSelectedOrder.forEach((row, index) => {
                        if (row.directie_id === directieId) {
                            this.filteredCompartimenteByIndex[index] = [...this.compartimenteByDirectie[directieId]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading compartimente:', error);
                    this.compartimenteByDirectie[directieId] = [];
                }
            });
    }

    /**
     * Load users filtered by directie and optionally compartiment
     */
    loadUsersForSelection(directieId: number, compartimentId?: number): void {
        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;

        // Check if already cached
        if (this.usersByDirectieCompartiment[cacheKey]) {
            return;
        }

        this.documentTypesService
            .getAvailableUsers(this.data.id, directieId, compartimentId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (res: any) => {
                    this.usersByDirectieCompartiment[cacheKey] = res.data || [];

                    // Update filtered lists for all rows that match this directie + compartiment combination
                    this.functiiSelectedOrder.forEach((row, index) => {
                        const rowCompartimentId = row.compartiment_id ?? undefined;
                        const matches = row.directie_id === directieId &&
                                      (compartimentId === rowCompartimentId ||
                                       (!compartimentId && !rowCompartimentId));

                        if (matches) {
                            this.filteredUsersByIndex[index] = [...this.usersByDirectieCompartiment[cacheKey]];
                        }
                    });
                },
                error: (error) => {
                    console.error('Error loading users:', error);
                    this.usersByDirectieCompartiment[cacheKey] = [];
                }
            });
    }

    /**
     * Get compartimente for a specific directie from cache
     */
    getCompartimenteForDirectie(directieId: number | null | undefined): CompartimentData[] {
        if (!directieId) return [];
        return this.compartimenteByDirectie[directieId] || [];
    }

    /**
     * Get users for a specific directie and optionally compartiment from cache
     */
    getUsersForSelection(directieId: number | null | undefined, compartimentId?: number | null): UserData[] {
        if (!directieId) return [];
        const cacheKey = compartimentId ? `${directieId}-${compartimentId}` : `${directieId}`;
        return this.usersByDirectieCompartiment[cacheKey] || [];
    }

    /**
     * Handle directie selection change - cascading effect
     */
    onDirectieChange(row: OrderSignature, index: number): void {
        // Reset dependent fields
        row.compartiment_id = null;
        row.user_id = null;

        // Load compartimente for the selected directie
        if (row.directie_id) {
            this.loadCompartimenteForDirectie(row.directie_id);
            // Also preload users for this directie (without compartiment)
            this.loadUsersForSelection(row.directie_id);
        }
    }

    /**
     * Handle compartiment selection change - cascading effect
     */
    onCompartimentChange(row: OrderSignature, index: number): void {
        // Reset dependent field
        row.user_id = null;

        // Load users for the selected directie + compartiment
        if (row.directie_id && row.compartiment_id) {
            // Convert null to undefined for method signature
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);
        } else if (row.directie_id) {
            // If compartiment is cleared, reload users for just the directie
            this.loadUsersForSelection(row.directie_id);
        }
    }

    /**
     * Handle user selection change (optional, for future enhancements)
     */
    onUserChange(row: OrderSignature, index: number): void {
        // Currently no action needed, but keeping for extensibility
        // Could be used for validation, logging, or other business logic
    }

    /**
     * Setup typeahead search control for a specific row
     */
    setupTypeaheadForRow(index: number): void {
        // Initialize search controls if not already done
        if (!this.directiiSearchControls[index]) {
            this.directiiSearchControls[index] = new FormControl('');
            this.compartimenteSearchControls[index] = new FormControl('');
            this.usersSearchControls[index] = new FormControl('');

            const row = this.functiiSelectedOrder[index];

            // Initialize filtered lists with full data
            this.filteredDirectiiByIndex[index] = [...this.directiiList];

            // Initialize compartimente list if directie is already selected
            if (row?.directie_id) {
                const compartimente = this.getCompartimenteForDirectie(row.directie_id);
                this.filteredCompartimenteByIndex[index] = [...compartimente];

                // Initialize users list if directie (and optionally compartiment) is selected
                const users = this.getUsersForSelection(row.directie_id, row.compartiment_id);
                this.filteredUsersByIndex[index] = [...users];
            } else {
                this.filteredCompartimenteByIndex[index] = [];
                this.filteredUsersByIndex[index] = [];
            }

            // Setup typeahead for Directii
            this.directiiSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    this.filteredDirectiiByIndex[index] = this.filterDirectii(searchTerm);
                });

            // Setup typeahead for Compartimente
            this.compartimenteSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    const currentRow = this.functiiSelectedOrder[index];
                    const availableCompartimente = this.getCompartimenteForDirectie(currentRow?.directie_id);
                    this.filteredCompartimenteByIndex[index] = this.filterCompartimente(searchTerm, availableCompartimente);
                });

            // Setup typeahead for Users
            this.usersSearchControls[index].valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(200),
                    distinctUntilChanged(),
                    takeUntil(this.unsubscribe$)
                )
                .subscribe(searchTerm => {
                    const currentRow = this.functiiSelectedOrder[index];
                    const availableUsers = this.getUsersForSelection(currentRow?.directie_id, currentRow?.compartiment_id);
                    this.filteredUsersByIndex[index] = this.filterUsers(searchTerm, availableUsers);
                });
        }
    }

    /**
     * Normalize string for accent-insensitive search
     */
    normalizeString(value: string): string {
        if (!value) return '';
        return value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim();
    }

    /**
     * Filter directii based on search term
     */
    filterDirectii(searchTerm: string): DirectieData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...this.directiiList];
        }
        const normalized = this.normalizeString(searchTerm);
        return this.directiiList.filter(directie =>
            this.normalizeString(directie.name).includes(normalized)
        );
    }

    /**
     * Filter compartimente based on search term
     */
    filterCompartimente(searchTerm: string, availableCompartimente: CompartimentData[]): CompartimentData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...availableCompartimente];
        }
        const normalized = this.normalizeString(searchTerm);
        return availableCompartimente.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalized)
        );
    }

    /**
     * Filter users based on search term
     */
    filterUsers(searchTerm: string, availableUsers: UserData[]): UserData[] {
        if (!searchTerm || searchTerm.trim() === '') {
            return [...availableUsers];
        }
        const normalized = this.normalizeString(searchTerm);
        return availableUsers.filter(user => {
            const name = user.name || user.full_name || '';
            return this.normalizeString(name).includes(normalized);
        });
    }

    /**
     * Called when directie dropdown opens - setup typeahead and preload data
     */
    onDirectieDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        // Reset search
        this.directiiSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when compartiment dropdown opens - setup typeahead and preload data
     */
    onCompartimentDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        const row = this.functiiSelectedOrder[index];

        if (row?.directie_id) {
            // Load compartimente if not cached
            this.loadCompartimenteForDirectie(row.directie_id);

            // Update filtered list immediately
            const availableCompartimente = this.getCompartimenteForDirectie(row.directie_id);
            this.filteredCompartimenteByIndex[index] = [...availableCompartimente];
        }

        // Reset search
        this.compartimenteSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when user dropdown opens - setup typeahead and preload data
     * Includes logic to preload list even if user is already selected
     */
    onUserDropdownOpened(index: number): void {
        this.setupTypeaheadForRow(index);
        const row = this.functiiSelectedOrder[index];

        if (row?.directie_id) {
            // Load users for current directie + compartiment combination
            // Convert null to undefined for method signature
            const compartimentId = row.compartiment_id ?? undefined;
            this.loadUsersForSelection(row.directie_id, compartimentId);

            // Update filtered list immediately (this allows seeing the list even if user is selected)
            const availableUsers = this.getUsersForSelection(row.directie_id, row.compartiment_id);
            this.filteredUsersByIndex[index] = [...availableUsers];
        }

        // Reset search
        this.usersSearchControls[index]?.setValue('', { emitEvent: true });
    }

    /**
     * Called when dropdown closes - clear search input
     */
    onDropdownClosed(index: number, type: 'directie' | 'compartiment' | 'user'): void {
        switch (type) {
            case 'directie':
                this.directiiSearchControls[index]?.setValue('', { emitEvent: false });
                break;
            case 'compartiment':
                this.compartimenteSearchControls[index]?.setValue('', { emitEvent: false });
                break;
            case 'user':
                this.usersSearchControls[index]?.setValue('', { emitEvent: false });
                break;
        }
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    save() {

        this.dialogRef?.close({
            success: true,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.componentService.setToken(token);
        });
    }

    updateSignaturesTemplateDocument(event: any) {
        event.stopPropagation();

        this.isLoading = true;
        let doc_type = this.destinationToggle === 'toPrimarie' ? 1 : 2;
        this.componentService
            .updateSignaturesTemplateOperatorIds(this.data.id, {
                doc_type: doc_type,
                user_type_ids: this.functiiOperator
                    .filter((fo: any) => fo.checked)
                    .map((fo: any) => fo.id),
            }, this.manual_override)
            .subscribe({
                next: () => {
                    this.loadDataTemplate();
                    this.saveClicked = true;
                },
                error: (err: any) => {
                    this.isLoading = false;
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    drop(event: CdkDragDrop<OrderSignature[]>): void {
        moveItemInArray(
            this.functiiSelectedOrder,
            event.previousIndex,
            event.currentIndex
        );

        // Update the order property of the reordered elements
        this.functiiSelectedOrder.forEach((item, index) => (item.order = index));
    }

    saveOrder(event: any) {
        event.stopPropagation();

        // Validate required fields before saving
        const validationErrors = this.validateSignatures();
        if (validationErrors.length > 0) {
            this.notificationService.warningSwal(
                this.errorTitle,
                validationErrors.join('<br>'),
                this.errorIcon
            );
            return;
        }

        this.isLoading = true;
        this.componentService
            .updateSignatures(this.functiiSelectedOrder, this.manual_override)
            .subscribe({
                next: () => {
                    console.log('saveOrder');
                    this.notificationService.successSwal(
                        this.successTitle,
                        'Semnăturile au fost salvate cu succes!'
                    );
                    this.loadDataTemplate();
                },
                error: (err: any) => {
                    this.isLoading = false;
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    /**
     * Validate that all signatures have required fields filled
     */
    validateSignatures(): string[] {
        const errors: string[] = [];

        this.functiiSelectedOrder.forEach((signature, index) => {
            const rowNumber = index + 1;

            // Validate functie (user_type) - required
            if (!signature.user_type) {
                errors.push(`Rândul ${rowNumber}: Funcția este obligatorie.`);
            }

            // Validate sign_type - required and not default value
            if (!signature.sign_type || signature.sign_type === '-1' || signature.sign_type === -1) {
                errors.push(`Rândul ${rowNumber} (${signature.functie}): Tipul de semnătură este obligatoriu.`);
            }

            // Validate directie_id - required
            if (!signature.directie_id) {
                errors.push(`Rândul ${rowNumber} (${signature.functie}): Direcția este obligatorie.`);
            }

            // Validate compartiment_id - required
            if (!signature.compartiment_id) {
                errors.push(`Rândul ${rowNumber} (${signature.functie}): Compartimentul este obligatoriu.`);
            }
        });

        return errors;
    }

    // Helper methods using the project's notification patterns
    private showSuccessMessage(message: string): void {
        Swal.fire({
            title: 'Succes',
            text: message,
            icon: 'success',
            confirmButtonText: this.helper.dialogConfig.buttonLabels.ok,
        });
    }

    private showErrorMessage(message: string): void {
        this.notificationService.warningSwal(
            this.errorTitle,
            message,
            this.errorIcon
        );
    }
}
